import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { VisibilityState } from '@tanstack/react-table';
import { KioskSessionsGrid } from '@/features/kiosk-sessions/components/KioskSessionsGrid';
import { KioskSessionsToolbar } from '@/features/kiosk-sessions/components/KioskSessionsToolbar';
import { KioskSessionDetailBlade } from '@/features/kiosk-sessions/components/KioskSessionDetailBlade';
import { useKioskSessionsStore } from '@/features/kiosk-sessions/stores/kiosk-sessions-store';
import { KioskSessionGridRow, KioskSessionFilters, SettlementFilter } from '@/features/kiosk-sessions/types/kiosk-session-types';
import { useOperatorStore } from '@/core/stores/operator-store';
import { useAuthStore } from '@/core/stores/auth-store';
import { toUtcStartOfDay, toUtcEndOfDay } from '@/features/kiosk-sessions/utils/date-utils';
import { defaultColumnVisibility, availableColumns } from '@/features/kiosk-sessions/components/GridColumnDefinitions';
import { exportKioskSessionsToExcel, exportKioskSessionsToExcelGrouped } from '@/core/utils/excelExport';
import { servicesContainer } from '@/core/di/services-container';
import { SlimLocation } from '@/features/kiosk-sessions/types/api-types';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/core/components/ui/alert-dialog';

const KioskReports = () => {
  const [searchParams] = useSearchParams();
  const {
    selectedOperator,
    operators,
    selectOperator,
    isLoading: operatorsLoading,
    loadOperators
  } = useOperatorStore();
  const { isDistributor } = useAuthStore();
  const selectedOperatorId = selectedOperator?.operatorId?.toString() || null;
  
  const [selectedSession, setSelectedSession] = useState<KioskSessionGridRow | null>(null);
  const [isDetailBladeOpen, setIsDetailBladeOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState('');

  // OCPI locations from slim API
  const [ocpiLocations, setOcpiLocations] = useState<SlimLocation[]>([]);

  // Sub-operators of the selected operator (Kiosk actors with ParentId = operator, names from Nayax Core)
  const [subOperators, setSubOperators] = useState<Array<{ actorId: number; name: string }>>([]);

  // Export confirmation dialog state
  const [showExportConfirmation, setShowExportConfirmation] = useState(false);
  const [pendingExportColumnVisibility, setPendingExportColumnVisibility] = useState<VisibilityState | null>(null);
  const [isExporting, setIsExporting] = useState(false);

  // Column visibility state - persisted to localStorage
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(() => {
    try {
      const saved = localStorage.getItem('kioskSessionsColumnVisibility');
      if (saved) {
        // Merge with defaults so columns added after the value was saved (e.g. phone/plate) stay hidden
        return { ...defaultColumnVisibility, ...JSON.parse(saved) };
      }
    } catch (error) {
      console.error('Failed to load column visibility from localStorage:', error);
    }
    return defaultColumnVisibility;
  });
  
  // Get sessionId from URL query parameters
  const sessionIdFromUrl = searchParams.get('sessionId');

  // Ref to prevent duplicate API calls during rapid re-renders
  const isLoadingRef = useRef(false);

  // Ref to track which transaction ID has already been searched server-side
  // Prevents infinite loops when server search also returns 0 results
  const transactionIdSearchedRef = useRef<string | undefined>(undefined);

  // Ref to track which session ID has already been searched server-side
  const sessionIdSearchedRef = useRef<string | undefined>(undefined);

  // Get default filters (current week)
  const getDefaultFilters = (): KioskSessionFilters => {
    const today = new Date();
    const weekAgo = new Date();
    weekAgo.setDate(today.getDate() - 7);

    return {
      dateFrom: toUtcStartOfDay(weekAgo),
      dateTo: toUtcEndOfDay(today),
      groupByLocation: false,
    };
  };

  const {
    sessions,
    groupedSessions,
    hierarchicalSessions,
    totalCount,
    currentPage,
    pageSize,
    pageSizeOptions,
    isLoading: sessionsLoading,
    isRefreshing: sessionsRefreshing,
    error: sessionsError,
    loadSessions,
    loadPage,
    setPageSize,
    loadGroupedSessions,
    refreshSessions,
    filters: storeFilters,
    setFilters: updateStoreFilters,
    hasMorePages,
    isLoadingMore,
    loadMoreSessions,
    groupByLocation,
    toggleGroupByLocation,
  } = useKioskSessionsStore();
  
  // Additional text filter state
  const [last4Digits, setLast4Digits] = useState<string | undefined>();
  const [evseIdFilter, setEvseIdFilter] = useState<string | undefined>();
  const [connectorIdFilter, setConnectorIdFilter] = useState<string | undefined>();
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [transactionIdFilter, setTransactionIdFilter] = useState<string | undefined>();
  const [sessionIdFilter, setSessionIdFilter] = useState<string | undefined>();
  const [settlementFilter, setSettlementFilter] = useState<SettlementFilter>('all');

  // Min energy filter (persisted to localStorage)
  const [minEnergyKwh, setMinEnergyKwh] = useState<string | undefined>(() => {
    try {
      const saved = localStorage.getItem('kioskMinEnergyKwh');
      return saved || undefined;
    } catch {
      return undefined;
    }
  });

  // Save column visibility to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem('kioskSessionsColumnVisibility', JSON.stringify(columnVisibility));
    } catch (error) {
      console.error('Failed to save column visibility to localStorage:', error);
    }
  }, [columnVisibility]);

  // Load operators for distributors
  useEffect(() => {
    if (isDistributor && operators.length === 0 && !operatorsLoading) {
      loadOperators();
    }
  }, [isDistributor, operators.length, operatorsLoading, loadOperators]);

  // Effect to fetch OCPI locations for the location dropdown
  useEffect(() => {
    if (!selectedOperatorId) return;

    const fetchOcpiLocations = async () => {
      try {
        const services = servicesContainer.getServices();
        const slimLocations: SlimLocation[] = await services.kioskSessionApi.getSlimLocations(selectedOperatorId);
        setOcpiLocations(slimLocations);
      } catch (error) {
        console.error('Failed to fetch OCPI locations:', error);
        setOcpiLocations([]);
      }
    };

    fetchOcpiLocations();
  }, [selectedOperatorId]);

  // Effect to fetch sub-operators from the actor hierarchy of the selected operator
  useEffect(() => {
    setSubOperators([]);
    if (!selectedOperatorId) return;

    let cancelled = false;
    const fetchSubOperators = async () => {
      try {
        const services = servicesContainer.getServices();
        const operatorId = Number(selectedOperatorId);
        const hierarchy = await services.actorApi.getActorHierarchy(selectedOperatorId);
        const list = hierarchy
          .filter(actor => actor.parentActorId === operatorId)
          .map(actor => ({ actorId: actor.actorId, name: actor.name || actor.actorId.toString() }));
        list.sort((a, b) => a.name.localeCompare(b.name));
        if (cancelled) return;
        setSubOperators(list);
      } catch (error) {
        console.error('Failed to fetch sub-operators:', error);
        if (!cancelled) setSubOperators([]);
      }
    };

    fetchSubOperators();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOperatorId]);

  // Effect to load sessions when operator or filters change - simplified pattern from sessions-report
  useEffect(() => {
    // Guard: Prevent duplicate API calls using ref
    if (isLoadingRef.current) {
      return;
    }

    // Guard: Prevent loading if already loading via state
    if (sessionsLoading || sessionsRefreshing) {
      return;
    }

    if (selectedOperatorId && storeFilters.dateFrom && storeFilters.dateTo) {
      // Set ref to prevent duplicate calls
      isLoadingRef.current = true;

      // Prepare filters with operatorId
      const filtersWithOperator = { ...storeFilters, operatorId: selectedOperatorId, limit: 25 };

      // Load grouped sessions or flat sessions based on groupByLocation toggle
      if (groupByLocation) {
        loadGroupedSessions(filtersWithOperator);
      } else {
        loadSessions(filtersWithOperator);
      }

      // Reset ref after a short delay to allow next legitimate change
      setTimeout(() => {
        isLoadingRef.current = false;
      }, 500);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedOperatorId,
    storeFilters.dateFrom,
    storeFilters.dateTo,
    storeFilters.locationId,
    storeFilters.subOperatorId,
    storeFilters.searchTerm,
    storeFilters.settlement,
    storeFilters.statuses,
    groupByLocation,
  ]); // Reload automatically when operator, dates, location, search, settlement, or status changes

  // Effect to handle sessionId from URL and auto-open detail blade
  useEffect(() => {
    if (sessionIdFromUrl && sessions && sessions.length > 0 && !sessionsLoading) {
      // Find the session with matching sessionId
      const matchingSession = sessions.find(session => session.session_id === sessionIdFromUrl);
      if (matchingSession && !isDetailBladeOpen) {
        setSelectedSession(matchingSession);
        setIsDetailBladeOpen(true);
      }
    }
  }, [sessionIdFromUrl, sessions, sessionsLoading, isDetailBladeOpen]);

  // Handle operator change (for distributors) - MUST BE BEFORE EARLY RETURNS
  const handleOperatorChange = useCallback((operatorId: string) => {
    const operator = operators.find(op => op.operatorId.toString() === operatorId);
    if (operator) {
      selectOperator(operator);
      // Clear location selection when operator changes
      setSelectedLocation('');
      // Reset filters for new operator
      updateStoreFilters({
        ...getDefaultFilters(),
        operatorId: operatorId,
        limit: 25,
        // Sub-operator belongs to the previous operator's hierarchy - reset to "all"
        subOperatorId: undefined
      });
    }
  }, [operators, selectOperator, updateStoreFilters]);

  const fallbackCurrency = useMemo(() => {
    const counts = new Map<string, number>();
    for (const s of sessions ?? []) {
      const c = s.currency;
      if (c && /^[A-Z]{3}$/.test(c)) {
        try {
          new Intl.NumberFormat('en', { style: 'currency', currency: c });
          counts.set(c, (counts.get(c) ?? 0) + 1);
        } catch { /* invalid */ }
      }
    }
    if (counts.size === 0) return undefined;
    return [...counts.entries()].reduce((a, b) => (b[1] > a[1] ? b : a))[0];
  }, [sessions]);

  // Apply client-side filtering for new text filters - MUST BE BEFORE EARLY RETURNS
  const filteredSessions = useMemo(() => {
    if (!sessions) return [];

    let filtered = sessions;

    // Status filter is now server-side (sent via storeFilters.statuses)

    // Last 4 Digits filter - check cardIdentifier first, fallback to last_4_digits
    if (last4Digits) {
      filtered = filtered.filter((session) => {
        if (session.cardIdentifier) {
          return session.cardIdentifier.includes(last4Digits);
        }
        return session.last_4_digits?.toString().includes(last4Digits) ?? false;
      });
    }

    // EVSE ID filter
    if (evseIdFilter) {
      const term = evseIdFilter.toLowerCase();
      filtered = filtered.filter(
        (session) => session.evse_id?.toLowerCase().includes(term)
      );
    }

    // Connector ID filter
    if (connectorIdFilter) {
      const term = connectorIdFilter.toLowerCase();
      filtered = filtered.filter(
        (session) => session.connector_id?.toLowerCase().includes(term)
      );
    }

    // Session ID filter
    if (sessionIdFilter) {
      const term = sessionIdFilter.toLowerCase();
      filtered = filtered.filter(
        (session) => session.session_id?.toLowerCase().includes(term)
      );
    }

    // Transaction ID filter
    if (transactionIdFilter) {
      const term = transactionIdFilter.toLowerCase();
      filtered = filtered.filter(
        (session) => session.payment_transaction_id?.toString().toLowerCase().includes(term)
      );
    }

    // Min energy filter
    if (minEnergyKwh) {
      const threshold = parseFloat(minEnergyKwh);
      if (!isNaN(threshold)) {
        filtered = filtered.filter((session) => session.total_energy > threshold);
      }
    }

    return filtered;
  }, [sessions, last4Digits, evseIdFilter, connectorIdFilter, sessionIdFilter, transactionIdFilter, minEnergyKwh]);

  // Calculate filtered count for display - MUST BE BEFORE EARLY RETURNS
  // For server-side pagination, always use totalCount from API (not local filtered length)
  const filteredCount = useMemo(() => {
    return totalCount; // Use API totalCount for server-side pagination
  }, [totalCount]);

  // Reset server-search ref whenever the transaction ID filter value changes.
  // When cleared after a server-side search, reload sessions without transactionId.
  useEffect(() => {
    transactionIdSearchedRef.current = undefined;
    if (!transactionIdFilter && storeFilters.transactionId !== undefined && selectedOperatorId && !sessionsLoading && !sessionsRefreshing) {
      loadSessions({
        ...storeFilters,
        transactionId: undefined,
        operatorId: selectedOperatorId,
        limit: 25,
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transactionIdFilter]);

  // Server-side fallback: when transaction ID filter yields no local results,
  // re-fetch from API with transactionId as a server-side parameter
  useEffect(() => {
    if (
      transactionIdFilter &&
      filteredSessions.length === 0 &&
      !sessionsLoading &&
      !sessionsRefreshing &&
      selectedOperatorId &&
      transactionIdSearchedRef.current !== transactionIdFilter
    ) {
      const numericId = parseInt(transactionIdFilter, 10);
      if (!isNaN(numericId)) {
        transactionIdSearchedRef.current = transactionIdFilter;
        loadSessions({
          ...storeFilters,
          operatorId: selectedOperatorId,
          transactionId: numericId,
          limit: 25,
        });
      }
    }
  }, [transactionIdFilter, filteredSessions.length, sessionsLoading, sessionsRefreshing, selectedOperatorId]);

  // Reset server-search ref when session ID filter value changes.
  // When cleared after a server-side search, reload sessions without sessionId.
  useEffect(() => {
    sessionIdSearchedRef.current = undefined;
    if (!sessionIdFilter && storeFilters.sessionId !== undefined && selectedOperatorId && !sessionsLoading && !sessionsRefreshing) {
      loadSessions({
        ...storeFilters,
        sessionId: undefined,
        operatorId: selectedOperatorId,
        limit: 25,
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionIdFilter]);

  // Server-side fallback: when session ID filter yields no local results,
  // re-fetch from API with sessionId as a server-side parameter
  useEffect(() => {
    if (
      sessionIdFilter &&
      filteredSessions.length === 0 &&
      !sessionsLoading &&
      !sessionsRefreshing &&
      selectedOperatorId &&
      sessionIdSearchedRef.current !== sessionIdFilter
    ) {
      sessionIdSearchedRef.current = sessionIdFilter;
      loadSessions({
        ...storeFilters,
        operatorId: selectedOperatorId,
        sessionId: sessionIdFilter,
        limit: 25,
      });
    }
  }, [sessionIdFilter, filteredSessions.length, sessionsLoading, sessionsRefreshing, selectedOperatorId]);



  const handleLocationSelect = (locationId: string) => {
    setSelectedLocation(locationId);

    // locationId is now the OCPI location ID directly from the dropdown
    if (locationId && locationId !== '') {
      updateStoreFilters({ locationId });
    } else {
      updateStoreFilters({ locationId: undefined });
    }
  };

  const handleSubOperatorSelect = (subOperatorId: string) => {
    updateStoreFilters({ subOperatorId: subOperatorId || undefined });
  };

  const handleRefresh = async () => {
    if (selectedOperatorId) {
      await refreshSessions();
    }
  };

  const handleRowSelect = (session: KioskSessionGridRow) => {
    setSelectedSession(session);
    setIsDetailBladeOpen(true);
  };

  const handleDetailBladeClose = () => {
    setIsDetailBladeOpen(false);
  };

  const handleFiltersChange = (newFilters: KioskSessionFilters) => {
    // Update store filters
    updateStoreFilters(newFilters);

    // Update selected location state if location filter changed
    if (newFilters.locationId && newFilters.locationId !== selectedLocation) {
      setSelectedLocation(newFilters.locationId);
    }
  };

  const handleGroupByLocationChange = (enabled: boolean) => {
    updateStoreFilters({ groupByLocation: enabled });

    if (selectedOperatorId) {
      toggleGroupByLocation();
    }
  };

  // Handle export to Excel with specified column visibility
  const handleExportToExcel = (exportColumnVisibility: VisibilityState) => {
    // Show confirmation dialog before exporting
    setPendingExportColumnVisibility(exportColumnVisibility);
    setShowExportConfirmation(true);
  };

  // Perform the actual export after confirmation
  const performExport = async () => {
    if (!pendingExportColumnVisibility) return;

    setShowExportConfirmation(false);
    setIsExporting(true);

    const exportToastId = toast.loading('Preparing export...', {
      description: 'Fetching all sessions across pages...'
    });

    try {
      const services = servicesContainer.getServices();

      if (groupByLocation && selectedOperatorId) {
        // Export grouped by location - fetch all sessions across all pages
        console.log('📥 Exporting grouped sessions...');

        const result = await services.getKioskSessionsUseCase.getAllSessionsGroupedForExport(
          storeFilters,
          250, // Max sessions per page (1000 caused 504 gateway timeouts)
          (current, total) => {
            // Update progress toast
            toast.loading(`Fetching sessions... ${current}/${total}`, {
              id: exportToastId,
              description: `Progress: ${Math.round((current / total) * 100)}%`
            });
          }
        );

        if (result.hasError) {
          toast.error('Export Failed', {
            id: exportToastId,
            description: result.errorMessage || 'Failed to fetch sessions for export'
          });
          return;
        }

        if (result.hierarchicalData.length === 0) {
          toast.warning('No Data to Export', {
            id: exportToastId,
            description: 'No sessions found matching the current filters'
          });
          return;
        }

        // Export the grouped data
        exportKioskSessionsToExcelGrouped(result.hierarchicalData, pendingExportColumnVisibility);

        toast.success('Export Complete', {
          id: exportToastId,
          description: `Exported ${result.totalCount} sessions grouped by location`
        });

      } else {
        // Export flat view - fetch all sessions across all pages
        console.log('📥 Exporting flat sessions...');

        const result = await services.getKioskSessionsUseCase.getAllSessionsForExport(
          storeFilters,
          250, // Max sessions per page (1000 caused 504 gateway timeouts)
          (current, total) => {
            // Update progress toast
            toast.loading(`Fetching sessions... ${current}/${total}`, {
              id: exportToastId,
              description: `Progress: ${Math.round((current / total) * 100)}%`
            });
          }
        );

        if (result.hasError) {
          toast.error('Export Failed', {
            id: exportToastId,
            description: result.errorMessage || 'Failed to fetch sessions for export'
          });
          return;
        }

        if (result.data.length === 0) {
          toast.warning('No Data to Export', {
            id: exportToastId,
            description: 'No sessions found matching the current filters'
          });
          return;
        }

        // Export the flat data
        exportKioskSessionsToExcel(result.data, pendingExportColumnVisibility);

        toast.success('Export Complete', {
          id: exportToastId,
          description: `Exported ${result.totalCount} sessions to Excel`
        });
      }

    } catch (error) {
      console.error('Failed to export data to Excel:', error);
      toast.error('Export Failed', {
        id: exportToastId,
        description: error instanceof Error ? error.message : 'An unexpected error occurred'
      });
    } finally {
      setIsExporting(false);
      setPendingExportColumnVisibility(null);
    }
  };

  // Handle reset filters
  const handleResetFilters = () => {
    updateStoreFilters({
      ...getDefaultFilters(),
      operatorId: selectedOperatorId || '',
      limit: 25,
      statuses: undefined,
      settlement: undefined,
      subOperatorId: undefined,
    });
    setSelectedLocation('');

    // Reset new text filters
    setLast4Digits(undefined);
    setEvseIdFilter(undefined);
    setConnectorIdFilter(undefined);
    setStatusFilter([]);
    setTransactionIdFilter(undefined);
    setSessionIdFilter(undefined);
    // Reset min energy filter
    handleMinEnergyChange(undefined);
    // Reset settlement filter
    setSettlementFilter('all');
  };

  // Handle search change
  const handleSearchChange = (searchTerm: string) => {
    updateStoreFilters({ searchTerm });
  };

  // Handle date range change
  const handleDateRangeChange = (range: { from: string; to: string }) => {
    updateStoreFilters({
      dateFrom: range.from,
      dateTo: range.to
    });
  };

  // Handle new text filter changes
  const handleLast4DigitsChange = (value: string | undefined) => {
    setLast4Digits(value);
  };

  const handleEvseIdChange = (value: string | undefined) => {
    setEvseIdFilter(value);
  };

  const handleConnectorIdChange = (value: string | undefined) => {
    setConnectorIdFilter(value);
  };

  const handleStatusChange = (values: string[]) => {
    setStatusFilter(values);
    // Send status filter server-side
    updateStoreFilters({ statuses: values.length > 0 ? values : undefined });
  };

  const handleTransactionIdChange = (value: string | undefined) => {
    setTransactionIdFilter(value);
  };

  const handleSessionIdChange = (value: string | undefined) => {
    setSessionIdFilter(value);
  };

  const handleMinEnergyChange = (value: string | undefined) => {
    setMinEnergyKwh(value);
    try {
      if (value) {
        localStorage.setItem('kioskMinEnergyKwh', value);
      } else {
        localStorage.removeItem('kioskMinEnergyKwh');
      }
    } catch {
      // ignore
    }
  };

  // Handle page change (matching sessions-report)
  const handlePageChange = (page: number) => {
    loadPage(page);
  };

  // Handle page size change (matching sessions-report)
  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
  };

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Only show content if operator is selected */}
      {selectedOperatorId && (
        <>
          {/* Toolbar */}
          <div className="flex-shrink-0">
            <KioskSessionsToolbar
              isDistributor={isDistributor}
              operators={operators}
              selectedOperatorId={selectedOperator?.operatorId?.toString()}
              onOperatorChange={handleOperatorChange}
              operatorsLoading={operatorsLoading}
              ocpiLocations={ocpiLocations}
              selectedLocation={selectedLocation}
              onLocationChange={handleLocationSelect}
              subOperators={subOperators}
              selectedSubOperatorId={storeFilters.subOperatorId || ''}
              onSubOperatorChange={handleSubOperatorSelect}
              dateRange={{
                from: storeFilters.dateFrom || toUtcStartOfDay(new Date()),
                to: storeFilters.dateTo || toUtcEndOfDay(new Date())
              }}
              onDateRangeChange={handleDateRangeChange}
              groupByLocation={groupByLocation}
              onGroupByLocationChange={handleGroupByLocationChange}
              searchQuery={storeFilters.searchTerm || ''}
              onSearchChange={handleSearchChange}
              last4Digits={last4Digits}
              onLast4DigitsChange={handleLast4DigitsChange}
              evseId={evseIdFilter}
              onEvseIdChange={handleEvseIdChange}
              connectorId={connectorIdFilter}
              onConnectorIdChange={handleConnectorIdChange}
              status={statusFilter}
              onStatusChange={handleStatusChange}
              sessionId={sessionIdFilter}
              onSessionIdChange={handleSessionIdChange}
              transactionId={transactionIdFilter}
              onTransactionIdChange={handleTransactionIdChange}
              minEnergyKwh={minEnergyKwh}
              onMinEnergyChange={handleMinEnergyChange}
              settlement={settlementFilter}
              onSettlementChange={(value: SettlementFilter) => {
                setSettlementFilter(value);
                updateStoreFilters({ settlement: value });
              }}
              onRefresh={handleRefresh}
              onReset={handleResetFilters}
              columnVisibility={columnVisibility}
              availableColumns={availableColumns}
              onExport={handleExportToExcel}
              onColumnVisibilityChange={setColumnVisibility}
              sessionCount={filteredCount}
              isLoading={sessionsLoading || sessionsRefreshing}
            />
          </div>

          {/* Grid Section - flex-1 to fill remaining space, overflow-hidden to constrain child */}
          <div className="flex-1 min-h-0 overflow-hidden px-6">
            <KioskSessionsGrid
              data={groupByLocation ? hierarchicalSessions : filteredSessions}
              groupedSessions={groupedSessions}
              groupByLocation={groupByLocation}
              isLoading={sessionsLoading}
              isRefreshing={sessionsRefreshing}
              error={sessionsError}
              onRowSelect={handleRowSelect}
              selectedSessionId={selectedSession?.session_id}
              totalCount={filteredCount}
              currentPage={currentPage}
              pageSize={pageSize}
              pageSizeOptions={pageSizeOptions}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              hasMorePages={hasMorePages}
              isLoadingMore={isLoadingMore}
              onLoadMore={loadMoreSessions}
              columnVisibility={columnVisibility}
              onColumnVisibilityChange={setColumnVisibility}
              onExport={handleExportToExcel}
            />
          </div>
        </>
      )}

      {!selectedOperatorId && (
        <div className="flex items-center justify-center py-16">
          <p className="text-gray-500">Please select an operator to view kiosk sessions.</p>
        </div>
      )}

      {/* Detail Blade */}
      <KioskSessionDetailBlade
        session={selectedSession}
        isOpen={isDetailBladeOpen}
        onClose={handleDetailBladeClose}
        fallbackCurrency={fallbackCurrency}
      />

      {/* Export Confirmation Dialog */}
      <AlertDialog open={showExportConfirmation} onOpenChange={setShowExportConfirmation}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Export All Sessions?</AlertDialogTitle>
            <AlertDialogDescription>
              This will export all sessions matching your current filters across all pages.
              {totalCount > 0 && (
                <div className="mt-2">
                  <span className="font-semibold">
                    Estimated: {totalCount.toLocaleString()} sessions
                  </span>
                  {storeFilters.searchTerm || storeFilters.last4Digits ? (
                    <div className="text-sm text-muted-foreground mt-1">
                      Note: Client-side filters (search, card digits) will be applied during export.
                    </div>
                  ) : null}
                </div>
              )}
              {totalCount > 1000 && (
                <span className="block mt-2 text-amber-600">
                  ⚠️ This is a large export and may take a few moments to complete.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isExporting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={performExport}
              disabled={isExporting}
              className="bg-[#FFCD00] hover:bg-[#E6B800] text-[#262626]"
            >
              {isExporting ? 'Exporting...' : 'Export to Excel'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default KioskReports;